import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Update } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, of, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Constants } from '../../core/constants';
import { BroadcastService } from '../../services/broadcast.service';
import { ActiveControllerInfo } from '../models/active-controller-info.model';
import { ConnectedDevice } from '../models/connected-device.model';
import { DailyWeatherModel } from '../models/daily-weather.model';
import { ToroAccountDetailed } from '../models/toro-account-detailed.model';
import { ToroAccountSimple } from '../models/toro-account-simple.model';
import { ToroLocationApanSetup } from '../models/toro-location-apan-setup.model';
import { ToroLocationDetailed } from '../models/toro-location-detailed.model';
import { ToroLocation } from '../models/toro-location.model';
import { LocationApiService } from '../services/location-api.service';
import { toroLocationActionTypes } from './../../accounts/store/toro-location/toro-location.actions';
import { ToroLocationState } from './../../accounts/store/toro-location/toro-location.entity';
import { getCurrentLocationDetail } from './../../accounts/store/toro-location/toro-location.selectors';
import { AccountManagerService } from './account-manager.service';
import { LocalCacheManager } from './local-cache.manager';
import { MailCenterManagerService } from './mail-center-manager.service';
import { NotificationManagerService } from './notification-manager.service';


@Injectable()
export class LocationManagerService implements OnDestroy {
    // Events
    currentLocationChange = new EventEmitter<ToroLocation>();
    gatewayConnectionStatusChange = new EventEmitter<ConnectedDevice>();

    broadcastServiceSubscription: Subscription;
    locationSubscription: Subscription;

    private _locations: { [accountId: number]: ToroLocation[] } = {};
    private _currentLocation: ToroLocation;
    private _weatherForecast: { [locationId: number]: { [date: string]: DailyWeatherModel } } = {};
    private _locationsDetailed: { [accountId: number]: ToroLocationDetailed[] } = {};
    private _activeControllers: { [locationId: number]: ActiveControllerInfo[] } = {};
    private interval;

    public isLoadingInformation = false;

    constructor(private locationApiService: LocationApiService,
        private broadcastService: BroadcastService,
        private accountManager: AccountManagerService,
        private mailCenterManager: MailCenterManagerService,
        private notificationManager: NotificationManagerService,
        private toroLocationStore: Store<ToroLocationState>,
        private localCacheManager: LocalCacheManager) {

        this.broadcastServiceSubscription = this.broadcastService.userSignedOut.subscribe(() => {
            this.clearCache();
        });

        this.locationSubscription = this.toroLocationStore.select(getCurrentLocationDetail).subscribe(loc => {
            this.currentLocation = loc;
        });
    }

    ngOnDestroy(): void {
        if (this.broadcastServiceSubscription) {
            this.broadcastServiceSubscription.unsubscribe();
        }
        if (this.locationSubscription) {
            this.locationSubscription.unsubscribe();
        }
    }

    private clearCache() {
        this.currentLocation = null;

        Object.keys(this._locations).forEach(key => {
            this._locations[key] = [];
        });
        this._locations = {};
    }

    public clearCurrentLocationDetails() {
        if (this.accountManager.currentAccount && this.accountManager.currentAccount !== null &&
            this._locationsDetailed && this._locationsDetailed[this.accountManager.currentAccount.Id] &&
            this._currentLocation && this._currentLocation !== null) {
            const currentInfo = this._locationsDetailed[this.accountManager.currentAccount.Id].find(loc => loc.Id === this._currentLocation.Id);
            const currentIndex = this._locationsDetailed[this.accountManager.currentAccount.Id].indexOf(currentInfo);

            if (currentIndex < 0) return;

            this._locationsDetailed[this.accountManager.currentAccount.Id].splice(currentIndex, 1);
        }
    }

    get currentLocation(): ToroLocation {

        if ((this._currentLocation && this._currentLocation.ConnectedDevice == undefined) &&
            (this.accountManager.currentAccount && (this.accountManager.currentAccount.ConnectedDevices && this.accountManager.currentAccount.ConnectedDevices !== null) && this.accountManager.currentAccount.ConnectedDevices.length > 0)) {
            let tempLocation = { ...this._currentLocation };
            tempLocation.ConnectedDevice = this.accountManager.currentAccount.ConnectedDevices
                .find(connectedDevice => connectedDevice.LocationId === this._currentLocation.Id);

            this._currentLocation = tempLocation;
        }
        return this._currentLocation;

    }

    set currentLocation(value: ToroLocation) {
        this._currentLocation = value;
        this.localCacheManager.selectedLocationId = value ? value.Id : null;

        // if (this._currentLocation && !this._weatherForecast[value.Id])
        if (this._currentLocation) {
            this.getLocationWeatherForecast(this._currentLocation.Latitude, this._currentLocation.Longitude)
                .subscribe((response: { [date: string]: DailyWeatherModel }) => {
                    this._weatherForecast[value.Id] = {};
                    Object.keys(response).forEach(dailyWeatherKey => {
                        this._weatherForecast[value.Id][dailyWeatherKey] = new DailyWeatherModel(response[dailyWeatherKey], dailyWeatherKey);
                    });
                });
        }
        if (this._currentLocation && !this.mailCenterManager.isMailCenterInitialized) {
            this.mailCenterManager.init(this._currentLocation.AccountId, this._currentLocation.Id);
        }
        this.currentLocationChange.emit(this._currentLocation);
        //this.startInterval();
    }

    get activeControllers(): { [locationId: number]: ActiveControllerInfo[] } {
        return this._activeControllers;
    }

    get weatherToday() {
        if (!this._currentLocation || !this._weatherForecast[this._currentLocation.Id]) {
            return null;
        }

        const weatherDataForLocation = this._weatherForecast[this._currentLocation.Id];
        return weatherDataForLocation[Object.keys(weatherDataForLocation)[this.getStartIndexForWeatherForecast(weatherDataForLocation)]];
    }

    getStartIndexForWeatherForecast(weatherDataForLocation) {
        const currentDateTime = moment.utc().format();
        let offset = this._currentLocation.UtcOffset;
        if (this._currentLocation.FirstDstChange !== null && this._currentLocation.LastDstChange !== null &&
            currentDateTime >= this._currentLocation.FirstDstChange && currentDateTime <= this._currentLocation.LastDstChange) {
            offset = offset + 1;
        }
        const now = moment().utc().add(offset, 'hours');
        const currentLocalDateTime = now.format('YYYY-MM-DD');
        return Object.keys(weatherDataForLocation).indexOf(currentLocalDateTime) === -1 ? 0 : Object.keys(weatherDataForLocation).indexOf(currentLocalDateTime);
    }

    get weatherForecast() {
        if (!this._currentLocation || !this._weatherForecast[this._currentLocation.Id]) {
            return null;
        }

        const weatherDataForLocation = this._weatherForecast[this._currentLocation.Id];

        const startIndex = this.getStartIndexForWeatherForecast(weatherDataForLocation) + 1;
        const endIndex = Object.keys(weatherDataForLocation).length < 7 ? Object.keys(weatherDataForLocation).length : startIndex + 6;

        const weatherForecast = [];
        for (let i = startIndex; i < endIndex; i++) {
            weatherForecast.push(weatherDataForLocation[Object.keys(weatherDataForLocation)[i]]);
        }
        return weatherForecast;
    }

    public getLocationsForAccount(account: ToroAccountSimple, cached: boolean = true): Observable<ToroLocation[]> {
        // Return cached data if it exists and has been requested. // TODO: Implement a caching logic in the future
        if (cached && this._locations && account.Id in this._locations) {
            return of(this._locations[account.Id]);
        }

        return this.accountManager.getAccountDetails(account)
            .pipe(
                map((detailedAccount: ToroAccountDetailed) => {

                    if (this._locations && account.Id in this._locations) {
                        if (this._locations[account.Id]) {
                            this._locations[account.Id] = [];
                        }
                    }
                    this._locations[account.Id] = [];
                    detailedAccount.Locations.forEach(location => {
                        if (location.ConnectedDevice === undefined || location.ConnectedDevice === null) {
                            location.ConnectedDevice = new ConnectedDevice(detailedAccount.ConnectedDevices.find(device => device.LocationId === location.Id));

                            let update: Update<ToroLocation> = {
                                id: location.Id,
                                changes: {
                                    ...location
                                }
                            }

                            this.toroLocationStore.dispatch(toroLocationActionTypes.updateLocationSuccess({ update: update }));
                        }
                        else {
                            location.ConnectedDevice = new ConnectedDevice(detailedAccount.ConnectedDevices.find(device => device.LocationId === location.Id));
                        }

                        this._locations[account.Id].push(location);
                    });

                    return detailedAccount.Locations;
                })
            );
    }

    public getLocation(locationId: number, cached: boolean = true): Observable<ToroLocation> {
        // Return cached data if it exists and has been requested.
        if (cached && this.currentLocation && this.currentLocation.Id === locationId) {
            return of(this.currentLocation);
        }

        return this.getLocations()
            .pipe(
                map(() => {
                    const objKeys = Object.keys(this._locations);

                    for (let i = 0; i < objKeys.length; i++) {
                        for (let j = 0; j < this._locations[objKeys[i]].length; j++) {
                            if (this._locations[objKeys[i]][j].Id === locationId) {
                                return this.currentLocation = this._locations[objKeys[i]][j];
                            }
                        }
                    }

                    return null;
                })
            );
    }

    public stopLocationIrrigation(locationId: number): Observable<{ success: boolean, error: string }> {
        return this.locationApiService.stopLocationIrrigation(locationId);
    }

    public getLocationDetails(accountId: number, locationId: number, cached: boolean = true): Observable<ToroLocationDetailed> {
        if (cached && this._locationsDetailed[accountId]) {
            const locationDetailed = this._locationsDetailed[accountId].find(loc => loc.Id === locationId);
            if (locationDetailed) {
                return of(locationDetailed);
            }
        }

        return this.locationApiService.getLocationDetails(locationId)
            .pipe(
                tap((locationDetailed: ToroLocationDetailed) => {
                    if (this._locationsDetailed[locationDetailed.AccountId]) {
                        const locationToUpdateIndex = this._locationsDetailed[locationDetailed.AccountId].findIndex(loc => loc.Id === locationDetailed.Id);

                        if (locationToUpdateIndex !== -1) {
                            this._locationsDetailed[locationDetailed.AccountId][locationToUpdateIndex] = new ToroLocationDetailed(locationDetailed);
                            return;
                        }

                        this._locationsDetailed[locationDetailed.AccountId].push(new ToroLocationDetailed(locationDetailed));
                        return;
                    }

                    this._locationsDetailed[locationDetailed.AccountId] = [];
                    this._locationsDetailed[locationDetailed.AccountId].push(new ToroLocationDetailed(locationDetailed));
                })
            );
    }

    public getGatewayStatus(accountId: number, locationId: number): Observable<ToroLocationDetailed> {
        // if (cached && this._locationsDetailed[accountId]) {
        //     const locationDetailed = this._locationsDetailed[accountId].find(loc => loc.Id === locationId);
        //     if (locationDetailed) {
        //         return of(locationDetailed);
        //     }
        // }

        return this.locationApiService.getGatewayStatus(locationId)
            .pipe(
                tap((locationDetailed: ToroLocationDetailed) => {
                    if (this._locationsDetailed[locationDetailed.AccountId]) {
                        const locationToUpdateIndex = this._locationsDetailed[locationDetailed.AccountId].findIndex(loc => loc.Id === locationDetailed.Id);

                        if (locationToUpdateIndex !== -1) {
                            this._locationsDetailed[locationDetailed.AccountId][locationToUpdateIndex] = new ToroLocationDetailed(locationDetailed);
                            return;
                        }

                        this._locationsDetailed[locationDetailed.AccountId].push(new ToroLocationDetailed(locationDetailed));
                        return;
                    }

                    this._locationsDetailed[locationDetailed.AccountId] = [];
                    this._locationsDetailed[locationDetailed.AccountId].push(new ToroLocationDetailed(locationDetailed));
                })
            );
    }

    public setDefaultThemeForScene(locationId: number): Observable<any> {
        if (locationId > 0)
            return this.locationApiService.setDefaultThemeForScenes(locationId);
    }

    // Helper Methods
    private getLocations(): Observable<{ [id: number]: ToroLocation[] }> {
        return this.accountManager.getAccountsList(true)
            .pipe(
                map(accounts => {
                    accounts.forEach(account => {
                        // Empty any existing locations previously fetched.
                        if (this._locations && account.Id in this._locations) {
                            if (this._locations[account.Id]) {
                                this._locations[account.Id].length = 0;
                            }
                        }
                        this._locations[account.Id] = [];

                        account.Locations.forEach(location => this._locations[account.Id].push(location));
                    });

                    return this._locations;
                })
            );
    }

    public getLocationCoordinates(address: string): Observable<any> {
        return this.locationApiService.getLocationCoordinates(address);
    }

    public createFullAddressString(address: string, city: string, state: string, zip: string): string {
        return `${address}, ${city}, ${state} ${zip}`;
    }

    public createLocation(accountId: number, location: ToroLocation): Observable<ToroLocation> {
        return this.locationApiService.createLocation(accountId, location);
    }

    public updateLocation(locationId: number, location: ToroLocation): Observable<ToroLocation> {
        return this.locationApiService.updateLocation(locationId, location);
    }

    public updateLocationWithApan(accountId: number, locationId: number, location: ToroLocationDetailed, area: ToroLocationApanSetup): Observable<ToroLocation> {
        return this.locationApiService.updateLocationWithApan(locationId, location, area)
            .pipe(
                tap(() => {
                    this._locationsDetailed[accountId].find(loc => loc.Id === locationId).Area = area;
                })
            );
    }

    public deleteLocation(locationId: number): Observable<any> {
        return this.locationApiService.deleteLocation(locationId);
    }

    private getLocationWeatherForecast(latitude: number, longitude: number): Observable<{ [date: string]: DailyWeatherModel }> {
        return this.locationApiService.getLocationWeatherForecast(latitude, longitude);
    }

    public getActiveControllersForLocation(locationId: number, cached: boolean = true): Observable<ActiveControllerInfo[]> {
        if (cached && this._activeControllers[locationId]) {
            return of(this._activeControllers[locationId]);
        }
        return this.locationApiService.getActiveControllers(locationId)
            .pipe(
                tap((activeControllers) => {
                    this._activeControllers[locationId] = activeControllers;
                })
            );
    }

    public stopInterval() {
        clearInterval(this.interval);
    }

    getServerAPIVersion() {
        return this.locationApiService.getServerAPIVersion();
    }
}
